import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  Target,
  BarChart3,
  Zap,
  Brain,
  Eye,
  Timer
} from 'lucide-react';
import { fetchFundingRates, fetchLiquidations, fetchLongShortRatio, fetchFearGreedIndex } from '../lib/coinglass';

interface SentimentData {
  score: number; // -100 to +100
  label: 'Extreme Fear' | 'Fear' | 'Neutral' | 'Greed' | 'Extreme Greed';
  confidence: number; // 0 to 1
  factors: {
    fundingRates: number;
    liquidations: number;
    longShortRatio: number;
    fearGreed: number;
  };
}

interface MarketSentimentAnalyzerProps {
  className?: string;
}

const MarketSentimentAnalyzer: React.FC<MarketSentimentAnalyzerProps> = ({ className = '' }) => {
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');

  // Fetch all required data
  const { data: fundingData } = useQuery({
    queryKey: ['sentiment-funding', selectedSymbol],
    queryFn: () => fetchFundingRates(selectedSymbol),
    refetchInterval: 60000,
  });

  const { data: liquidationData } = useQuery({
    queryKey: ['sentiment-liquidations'],
    queryFn: () => fetchLiquidations(),
    refetchInterval: 60000,
  });

  const { data: longShortData } = useQuery({
    queryKey: ['sentiment-longsshort', selectedSymbol],
    queryFn: () => fetchLongShortRatio('Binance', `${selectedSymbol}USDT`),
    refetchInterval: 60000,
  });

  const { data: fearGreedData } = useQuery({
    queryKey: ['sentiment-feargreed'],
    queryFn: () => fetchFearGreedIndex(),
    refetchInterval: 300000, // 5 minutes
  });

  // Calculate sentiment score
  const calculateSentiment = (): SentimentData => {
    if (!fundingData || !liquidationData || !longShortData) {
      return {
        score: 0,
        label: 'Neutral',
        confidence: 0,
        factors: { fundingRates: 0, liquidations: 0, longShortRatio: 0, fearGreed: 0 }
      };
    }

    // Funding Rate Analysis (-50 to +50)
    const avgFundingRate = fundingData.reduce((sum, item) => sum + item.fundingRate, 0) / fundingData.length;
    const fundingScore = Math.max(-50, Math.min(50, avgFundingRate * 10000)); // Scale to -50/+50

    // Liquidation Analysis (-30 to +30)
    const symbolLiquidation = liquidationData.find(item => item.symbol.toUpperCase() === selectedSymbol.toUpperCase());
    const longLiqRatio = symbolLiquidation ? 
      symbolLiquidation.long_liquidation_usd_24h / (symbolLiquidation.liquidation_usd_24h || 1) : 0.5;
    const liquidationScore = (longLiqRatio - 0.5) * 60; // Scale to -30/+30

    // Long/Short Ratio Analysis (-40 to +40)
    const longRatio = longShortData.longRatio || 0.5;
    const longShortScore = (longRatio - 0.5) * 80; // Scale to -40/+40

    // Fear & Greed Index (-25 to +25)
    const fearGreedValue = fearGreedData?.[0]?.value || 50;
    const fearGreedScore = (fearGreedValue - 50) * 0.5; // Scale to -25/+25

    // Calculate total score
    const totalScore = Math.round(fundingScore + liquidationScore + longShortScore + fearGreedScore);
    const clampedScore = Math.max(-100, Math.min(100, totalScore));

    // Determine label
    let label: SentimentData['label'];
    if (clampedScore <= -60) label = 'Extreme Fear';
    else if (clampedScore <= -20) label = 'Fear';
    else if (clampedScore <= 20) label = 'Neutral';
    else if (clampedScore <= 60) label = 'Greed';
    else label = 'Extreme Greed';

    // Calculate confidence based on data availability
    const dataAvailability = [fundingData.length > 0, !!symbolLiquidation, !!longShortData, !!fearGreedData]
      .filter(Boolean).length / 4;
    
    return {
      score: clampedScore,
      label,
      confidence: dataAvailability,
      factors: {
        fundingRates: Math.round(fundingScore),
        liquidations: Math.round(liquidationScore),
        longShortRatio: Math.round(longShortScore),
        fearGreed: Math.round(fearGreedScore)
      }
    };
  };

  const sentiment = calculateSentiment();

  const getSentimentColor = (score: number) => {
    if (score <= -60) return 'text-red-500';
    if (score <= -20) return 'text-orange-500';
    if (score <= 20) return 'text-gray-400';
    if (score <= 60) return 'text-green-500';
    return 'text-emerald-400';
  };

  const getSentimentBgColor = (score: number) => {
    if (score <= -60) return 'from-red-500 to-red-600';
    if (score <= -20) return 'from-orange-500 to-red-500';
    if (score <= 20) return 'from-gray-500 to-gray-600';
    if (score <= 60) return 'from-green-500 to-emerald-500';
    return 'from-emerald-400 to-green-500';
  };

  const getFactorIcon = (factorName: string) => {
    switch (factorName) {
      case 'fundingRates': return <Target className="w-4 h-4" />;
      case 'liquidations': return <AlertCircle className="w-4 h-4" />;
      case 'longShortRatio': return <BarChart3 className="w-4 h-4" />;
      case 'fearGreed': return <Brain className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getFactorLabel = (factorName: string) => {
    switch (factorName) {
      case 'fundingRates': return 'Funding Rates';
      case 'liquidations': return 'Liquidations';
      case 'longShortRatio': return 'Long/Short Ratio';
      case 'fearGreed': return 'Fear & Greed';
      default: return factorName;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">Market Sentiment Analyzer</h3>
          <p className="text-sm text-gray-400">AI-powered sentiment analysis from multiple data sources</p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
          >
            <option value="BTC">Bitcoin</option>
            <option value="ETH">Ethereum</option>
            <option value="SOL">Solana</option>
          </select>
        </div>
      </div>

      {/* Main Sentiment Display */}
      <motion.div
        className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-lg p-6"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="text-center">
          {/* Sentiment Score */}
          <motion.div
            className="relative mx-auto mb-6"
            style={{ width: 200, height: 200 }}
          >
            {/* Circular Progress */}
            <svg className="transform -rotate-90 w-full h-full">
              <circle
                cx="100"
                cy="100"
                r="80"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-zinc-700"
              />
              <motion.circle
                cx="100"
                cy="100"
                r="80"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeLinecap="round"
                className={getSentimentColor(sentiment.score)}
                initial={{ strokeDasharray: 0, strokeDashoffset: 0 }}
                animate={{ 
                  strokeDasharray: `${Math.abs(sentiment.score) * 5.02} 502`,
                  strokeDashoffset: sentiment.score < 0 ? 251 : 0
                }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
            </svg>
            
            {/* Center Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.div
                className={`text-4xl font-bold ${getSentimentColor(sentiment.score)}`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                {sentiment.score > 0 ? '+' : ''}{sentiment.score}
              </motion.div>
              <div className="text-sm text-gray-400">Sentiment Score</div>
            </div>
          </motion.div>

          {/* Sentiment Label */}
          <motion.div
            className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${getSentimentBgColor(sentiment.score)} text-white font-semibold mb-4`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            {sentiment.score > 0 ? (
              <TrendingUp className="w-4 h-4 mr-2" />
            ) : sentiment.score < 0 ? (
              <TrendingDown className="w-4 h-4 mr-2" />
            ) : (
              <Activity className="w-4 h-4 mr-2" />
            )}
            {sentiment.label}
          </motion.div>

          {/* Confidence */}
          <div className="text-sm text-gray-400">
            Confidence: {(sentiment.confidence * 100).toFixed(0)}%
          </div>
        </div>
      </motion.div>

      {/* Factor Breakdown */}
      <motion.div
        className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-lg p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Eye className="w-5 h-5 mr-2" />
          Factor Analysis
        </h4>
        
        <div className="space-y-4">
          {Object.entries(sentiment.factors).map(([factor, score], index) => (
            <motion.div
              key={factor}
              className="flex items-center justify-between"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg bg-gradient-to-br ${getSentimentBgColor(score)}`}>
                  {getFactorIcon(factor)}
                </div>
                <span className="text-white font-medium">{getFactorLabel(factor)}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-32 bg-zinc-700 rounded-full h-2">
                  <motion.div
                    className={`h-full rounded-full bg-gradient-to-r ${getSentimentBgColor(score)}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.abs(score)}%` }}
                    transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                  />
                </div>
                <span className={`text-sm font-bold ${getSentimentColor(score)} min-w-[40px] text-right`}>
                  {score > 0 ? '+' : ''}{score}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Recommendations */}
      <motion.div
        className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-lg p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Brain className="w-5 h-5 mr-2" />
          AI Insights
        </h4>
        
        <div className="space-y-3">
          {sentiment.score <= -60 && (
            <div className="flex items-start space-x-3 p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <div className="text-red-300 font-medium">Extreme Fear Detected</div>
                <div className="text-sm text-red-400">
                  Market showing signs of capitulation. Consider DCA strategies and risk management.
                </div>
              </div>
            </div>
          )}
          
          {sentiment.score >= 60 && (
            <div className="flex items-start space-x-3 p-3 bg-yellow-900/20 border border-yellow-800 rounded-lg">
              <Zap className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <div className="text-yellow-300 font-medium">Extreme Greed Warning</div>
                <div className="text-sm text-yellow-400">
                  Market may be overheated. Consider taking profits and reducing leverage.
                </div>
              </div>
            </div>
          )}
          
          {Math.abs(sentiment.score) <= 20 && (
            <div className="flex items-start space-x-3 p-3 bg-blue-900/20 border border-blue-800 rounded-lg">
              <Target className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <div className="text-blue-300 font-medium">Neutral Market</div>
                <div className="text-sm text-blue-400">
                  Market sentiment is balanced. Good time for patient accumulation.
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-start space-x-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
            <Timer className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-gray-300 font-medium">Next Update</div>
              <div className="text-sm text-gray-400">
                Sentiment updates every minute. Factor weights may change based on market conditions.
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default React.memo(MarketSentimentAnalyzer);
